// Module 16(b) §11 — the five-step wizard state machine (`DataExportCreate.vue:1-504`, 681-701).
//
// Legacy held 36 sibling `ref`s on the host and re-derived every gate from them inline
// (`handleNextDisabled`, `:323-367`). Here the ONLY state the machine owns is the step index, the
// Schedule step's UI model and the two transient signals a step cannot write onto the draft
// (`destinationValid`, the block message). Every gate reads `store.draft`.
//
// The four predicates below are exported as PURE functions so the gating can be tested without a
// component: they are the ported semantics of `:335-373`, minus the code.
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'

import { CreateExportJobFlow, type CreateExportJobOutcome } from '../flows/CreateExportJobFlow'
import type { ExportScheduleDraft, ExportSummaryData } from '../models/export.types'
import type { ExportColumnSelection, ExportJobDraft } from '../models/exportJob.types'
import {
  PLATFORM_LABEL_KEYS,
  UPDATE_MODE_LABEL_KEYS,
  resolveExportLabel,
} from '../service/exportJobLabels'
import { useDataExportStore, type DataExportStore } from '../stores/dataExportStore'

import { customExportTemplates } from './useExportColumns'
import {
  DEFAULT_EXPORT_SCHEDULE,
  availableExportFrequencies,
  useScheduleTimeInfo,
} from './useScheduleTimeInfo'

import { useRbac, useWorkspace } from '@/features/account'
import { useI18n } from '@/shared/i18n'

/** `DataExportStep4.vue:145-152` — the frequency select is an entitlement, not a fixed list. */
export const FREQUENCIES_PERMISSION = 'export_hub_frequencies'

/**
 * `DataExportCreate.vue:193` — `getWorkspaceAttributesByKey('mmp_events')?.value || []`, the event
 * catalog the Metric Configuration multi-select offers. The attribute reaches the client as BOTH a
 * flat array and a `{platform: [event]}` map (v3's two call sites disagree on the fallback: `[]` at
 * `DataExportCreate.vue:193` vs `{}` at `CustomCohortCreate.vue:55`), so both shapes are flattened.
 */
export function readMmpEvents(raw: unknown): string[] {
  const values = raw !== null && typeof raw === 'object' ? Object.values(raw).flat() : []
  return [...new Set(values.filter((value): value is string => typeof value === 'string'))]
}

export const EXPORT_WIZARD_STEPS = [1, 2, 3, 4, 5] as const
export type ExportWizardStep = (typeof EXPORT_WIZARD_STEPS)[number]

/** `:335-336` + `:369-373` — a name, at least one platform, and an account for EVERY platform. */
export function isSourceComplete(draft: ExportJobDraft): boolean {
  return (
    draft.name.trim() !== '' &&
    draft.platforms.length > 0 &&
    draft.platforms.every((platform) => (draft.selectedAccounts[platform] ?? []).length > 0)
  )
}

function hasEventSlug(column: ExportColumnSelection): boolean {
  const selected = column.selected_event_slug
  return Array.isArray(selected) ? selected.length > 0 : Boolean(selected)
}

/**
 * `:341-347` + the gate `:349-354` that never ran.
 *
 * LIVE HALF (parity): a metric that declares `event_slug` is configured once it carries EITHER a
 * `selected_event_slug` OR a `selected_cohort_period` — legacy's `hasEvent || hasCohort` (`:346`),
 * an OR and not an AND.
 *
 * **FIXED HALF — the required-cohort gate is dead in v3 and now actually runs.** `:351` read
 * `dataExportStep2Ref.value.areAllRequiredCohortsFilled`, but `DataExportStep2.vue` carries no
 * `defineExpose` (0 grep hits repo-wide) and a `<script setup>` child exposes nothing, so the
 * property was permanently `undefined`, the `!== undefined` guard short-circuited, and the check
 * never fired. Enforcing it is a BEHAVIOUR CHANGE, not parity: a configuration that submits today
 * with an unfilled required cohort is now blocked at Data → Destination.
 *
 * The state is read off the DRAFT COLUMN (`selected_cohort_period`, written by `MetricConfigPanel`
 * through `applyMetricConfig`), never off a child component instance — which is exactly how the
 * v3 version was able to rot unnoticed.
 */
export function isMetricConfigured(column: ExportColumnSelection): boolean {
  if (column.is_cohort_required && !column.selected_cohort_period) return false
  if (!column.numeric || !column.event_slug) return true
  return hasEventSlug(column) || Boolean(column.selected_cohort_period)
}

/** `:338-357` — a template, at least one column, and every configurable metric configured. */
export function isDataComplete(draft: ExportJobDraft): boolean {
  return (
    draft.template !== null && draft.columns.length > 0 && draft.columns.every(isMetricConfigured)
  )
}

/**
 * `:359-364` — an update mode, plus project AND dataset for BigQuery. The e-mail half of the gate
 * cannot be derived from the draft (an invalid list is written back as `emails: []`, which is
 * indistinguishable from "not typed yet"), so `DestinationStep`'s `update:valid` is ANDed on top
 * by the machine below — see `destinationValid`.
 */
export function isDestinationComplete(draft: ExportJobDraft): boolean {
  const destination = draft.destination
  if (destination.type === null || destination.update_mode === null) return false
  if (destination.type === 'big_query') {
    return Boolean(destination.project_id) && Boolean(destination.dataset_id)
  }
  return true
}

/** `:366` — a frequency. */
export function isScheduleComplete(draft: ExportJobDraft): boolean {
  return draft.schedule.frequency !== null
}

export interface UseExportWizardOptions {
  store?: DataExportStore
  /** Injected in specs; production constructs the real flow lazily on first `complete()`. */
  createFlow?: () => { execute: () => Promise<CreateExportJobOutcome> }
}

export interface UseExportWizard {
  draft: ComputedRef<ExportJobDraft | null>
  step: Ref<ExportWizardStep>
  /** The Schedule step's UI model; its `time_info` derivation is written onto the draft. */
  schedule: Ref<ExportScheduleDraft>
  /** `export_hub_frequencies` — what the Schedule step is allowed to offer. */
  allowedFrequencies: ComputedRef<readonly string[]>
  /** The workspace's `mmp_events` catalog, handed to the Data step's metric configuration. */
  availableEvents: ComputedRef<readonly string[]>
  summary: ComputedRef<ExportSummaryData>
  /** Bound from `DestinationStep`'s `update:valid` — the e-mail validity v3 computed and ignored. */
  destinationValid: Ref<boolean>
  /** A blocked advance's message key; cleared on the next successful move. */
  blockedKey: Ref<string | null>
  submitting: Ref<boolean>
  isStepAllowed: (step: number) => boolean
  nextDisabled: (step: number) => boolean
  next: () => Promise<void>
  back: () => void
  goTo: (step: number) => void
  complete: () => Promise<CreateExportJobOutcome>
  reset: () => void
}

export function useExportWizard(options: UseExportWizardOptions = {}): UseExportWizard {
  const store = options.store ?? useDataExportStore()
  const { t } = useI18n()
  const { permissions } = useRbac()
  const { getAttribute } = useWorkspace()
  // The machine is the draft's owner: a wizard opened without one would gate every step on `null`.
  if (!store.draft) store.startDraft()

  const step = ref<ExportWizardStep>(1)
  const schedule = ref<ExportScheduleDraft>({ ...DEFAULT_EXPORT_SCHEDULE })
  const destinationValid = ref(false)
  const blockedKey = ref<string | null>(null)
  const submitting = ref(false)
  const draft = computed(() => store.draft)

  const allowedFrequencies = computed<readonly string[]>(() => {
    const value = permissions.value[FREQUENCIES_PERMISSION]
    return Array.isArray(value) ? value.filter((entry) => typeof entry === 'string') : []
  })
  const availableEvents = computed<readonly string[]>(() =>
    readMmpEvents(getAttribute('mmp_events')?.value),
  )

  // The Schedule step edits the UI model; the draft carries the payload-facing half. Immediate so
  // the frequency the step PRESELECTS is on the draft before the user reaches step 4.
  watch(
    schedule,
    (next) => {
      store.updateDraft({
        schedule: { frequency: next.frequency, time_info: useScheduleTimeInfo(next) },
      })
    },
    { immediate: true, deep: true },
  )

  // `DEFAULT_EXPORT_SCHEDULE.frequency` is `once`, which an account may not be entitled to. Snap
  // the preselection onto the first frequency the select will actually offer, so the control never
  // displays a value absent from its own options (v3 started empty and simply blocked Next).
  watch(
    allowedFrequencies,
    (allowed) => {
      const offered = availableExportFrequencies(allowed)
      if (offered.length === 0) return
      if (offered.some((option) => option.value === schedule.value.frequency)) return
      schedule.value = { ...schedule.value, frequency: offered[0]!.value }
    },
    { immediate: true },
  )

  function satisfied(index: number): boolean {
    const current = draft.value
    if (!current) return false
    if (index === 1) return isSourceComplete(current)
    if (index === 2) return isDataComplete(current)
    if (index === 3) return isDestinationComplete(current) && destinationValid.value
    if (index === 4) return isScheduleComplete(current)
    return true
  }

  /** A step is reachable only when every step before it is satisfied (`:323-367` composed). */
  function isStepAllowed(target: number): boolean {
    if (target <= 1) return true
    if (target > EXPORT_WIZARD_STEPS.length) return false
    for (let index = 1; index < target; index += 1) {
      if (!satisfied(index)) return false
    }
    return true
  }

  function nextDisabled(target: number): boolean {
    return !satisfied(target)
  }

  /**
   * **FIXED — the duplicate-name race (`:682-698`).** v3 raised its warning from a watcher that
   * fired AFTER `handleNext` (`:464-468`) had already incremented `modalStep`, so the user saw the
   * message AND landed on an empty Step 2. Both checks now BLOCK the advance.
   *
   * The template probe is the same guard's other half: the catalog is platform-scoped, so it is
   * loaded here rather than on Step 2's mount — otherwise "no template for this platform" could
   * only ever be discovered after the step had already changed.
   */
  async function leaveSource(): Promise<boolean> {
    const current = draft.value
    if (!current) return false
    if (store.isNameTaken(current.name)) {
      blockedKey.value = 'dataExport.wizard.duplicateName'
      return false
    }
    try {
      await store.loadTemplates(current.platforms)
    } catch {
      blockedKey.value = 'dataExport.wizard.templatesFailed'
      return false
    }
    if (customExportTemplates(store.templates).length === 0) {
      blockedKey.value = 'dataExport.wizard.noTemplates'
      return false
    }
    return true
  }

  async function next(): Promise<void> {
    const current = step.value
    if (nextDisabled(current) || current === EXPORT_WIZARD_STEPS.length) return
    if (current === 1 && !(await leaveSource())) return
    blockedKey.value = null
    step.value = (current + 1) as ExportWizardStep
  }

  function back(): void {
    blockedKey.value = null
    if (step.value > 1) step.value = (step.value - 1) as ExportWizardStep
  }

  function goTo(target: number): void {
    if (!EXPORT_WIZARD_STEPS.includes(target as ExportWizardStep)) return
    // Backwards is always free; forwards only through a satisfied prefix.
    if (target > step.value && !isStepAllowed(target)) return
    blockedKey.value = null
    step.value = target as ExportWizardStep
  }

  const summary = computed<ExportSummaryData>(() => {
    const current = draft.value
    const destination = current?.destination
    return {
      name: current?.name ?? '',
      platforms: (current?.platforms ?? []).map((platform) =>
        resolveExportLabel(PLATFORM_LABEL_KEYS, platform, t),
      ),
      columns: (current?.columns ?? []).map((column) => ({
        header: column.header,
        numeric: column.numeric,
      })),
      filterCount: (current?.filters ?? []).filter((filter) => Boolean(filter.value)).length,
      firstDate: current?.dateRange.firstDate ?? '',
      lastDate: current?.dateRange.lastDate ?? '',
      destination: destination?.type ?? 'email',
      updateMode: resolveExportLabel(UPDATE_MODE_LABEL_KEYS, destination?.update_mode ?? '', t),
      frequency: current?.schedule.frequency ?? 'once',
      recipients: destination?.emails ?? [],
      projectName: destination?.project_id ?? '',
      dataset: destination?.dataset_id ?? null,
    }
  })

  /**
   * The create use case is the FLOW's (`CreateExportJobFlow.execute` owns the pre-flight limit
   * guard, the payload build and the POST). The machine only refreshes what the create invalidated;
   * the caller renders the outcome — a `limitReached` is the upgrade modal, never a toast (§12).
   */
  async function complete(): Promise<CreateExportJobOutcome> {
    const flow = options.createFlow?.() ?? new CreateExportJobFlow({ store })
    submitting.value = true
    try {
      const outcome = await flow.execute()
      if (outcome.status === 'created') {
        // The jobs list and the quota tiles both read this store, so reloading it IS the reload.
        // `allSettled`: the export EXISTS at this point, and a failed refresh of a list the user is
        // about to be returned to must not be reported as a failed create. A refresh that does not
        // land leaves exactly the pre-create view, which the list's own Retry already recovers.
        await Promise.allSettled([store.loadJobs(), store.loadLimits()])
      }
      return outcome
    } finally {
      submitting.value = false
    }
  }

  /**
   * v3's `resetExport()` was 37 hand-written lines re-assigning 30 refs (`:229-265`) — including
   * one asymmetry it never noticed (the initial `selectedDateOption` carries `allowed: ['once']`
   * at `:212`, the reset's replacement drops it). The draft factory owns those defaults now, so
   * dropping the draft IS the reset; the next `useExportWizard` mints a fresh one.
   */
  function reset(): void {
    store.resetDraft()
    step.value = 1
    schedule.value = { ...DEFAULT_EXPORT_SCHEDULE }
    destinationValid.value = false
    blockedKey.value = null
  }

  return {
    draft,
    step,
    schedule,
    allowedFrequencies,
    availableEvents,
    summary,
    destinationValid,
    blockedKey,
    submitting,
    isStepAllowed,
    nextDisabled,
    next,
    back,
    goTo,
    complete,
    reset,
  }
}
